import React, { useState, useEffect, useCallback } from 'react';
import { Star, TrendingUp, Award, ShoppingCart, Loader, Info, MapPin, Clock, Navigation, Zap, Package, Flame, Plus, Minus } from 'lucide-react';
import { TOP_RATED_FOODS_URL, TRENDING_FOODS_URL, RECOMMENDED_FOODS_URL, CART_ADD_URL, CART_VIEW_URL, CART_UPDATE_URL, CART_REMOVE_ITEM_URL } from '../../config/api';
import { authenticatedFetch, getFirstImageUrl, FOOD_FALLBACK_IMG } from '../Utils/apiHelpers';
import { trackFood, trackError } from '../Utils/analytics';
import { getStoredCoords } from '../Utils/location';
import FoodDetailModal from '../FoodDetailModal/FoodDetailModal';
import './FoodDiscovery.css';

type Tab = 'recommended' | 'top_rated' | 'trending';

interface DiscoveryFood {
  food_id: number;
  food_name: string;
  image_urls?: string | string[];
  food_image?: string;
  price?: number;
  average_rating?: number;
  total_reviews?: number;
  total_orders?: number;
  category?: string;
  description?: string;
  available_quantity?: number;
  recommendation_score?: number;
  trending_score?: number;
  rating_score?: number;
  preparation_time?: string;
  pickup_location?: string;
  serving_size?: string;
  stock_status?: string;
  badges?: string[];
  distance_km?: number;
  eta?: string;
  delivery_status?: string;
  is_available?: boolean;
  kitchen?: { kitchen_id: number; kitchen_name: string; city?: string };
}

interface FoodDiscoveryProps {
  onCartChange?: () => void;
  showNotification?: (type: 'success' | 'error' | 'info' | 'warning', title: string, message: string) => void;
  cartRefreshToken?: number;
  locationVersion?: number;
}

interface CartItemState {
  quantity: number;
  cartId: number;
  loading: boolean;
}

const FALLBACK = FOOD_FALLBACK_IMG;

const FoodDiscovery: React.FC<FoodDiscoveryProps> = ({ onCartChange, showNotification, cartRefreshToken = 0, locationVersion = 0 }) => {
  const [activeTab, setActiveTab] = useState<Tab>('recommended');
  const [foods, setFoods] = useState<DiscoveryFood[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [detailFood, setDetailFood] = useState<DiscoveryFood | null>(null);
  const [cartMap, setCartMap] = useState<Record<number, CartItemState>>({});
  const [visibleCount, setVisibleCount] = useState(8);
  const [radius, setRadius] = useState<number>(() => {
    const stored = localStorage.getItem('foodDiscoveryRadius');
    return stored ? parseInt(stored, 10) : 15;
  });
  const isLoggedIn = !!localStorage.getItem('authToken');

  const syncCart = useCallback(async () => {
    if (!isLoggedIn) return;
    try {
      const res = await authenticatedFetch(CART_VIEW_URL);
      if (!res.ok) return;
      const json = await res.json();
      if (!json.success) return;
      const items: Array<{ food_id: number; cart_id: number; quantity: number }> = json.items || [];
      const map: Record<number, CartItemState> = {};
      items.forEach(item => {
        map[item.food_id] = { quantity: item.quantity, cartId: item.cart_id, loading: false };
      });
      setCartMap(map);
    } catch { /* non-critical */ }
  }, [isLoggedIn]);

  useEffect(() => {
    syncCart();
  }, [syncCart, cartRefreshToken]);

  const setItemLoading = (foodId: number, loading: boolean) => {
    setCartMap(prev => prev[foodId] ? { ...prev, [foodId]: { ...prev[foodId], loading } } : prev);
  };

  const handleIncrement = async (food: DiscoveryFood) => {
    if (!isLoggedIn) {
      showNotification?.('info', 'Login Required', 'Please log in to add items to your cart.');
      return;
    }
    const current = cartMap[food.food_id];
    if (current && current.loading) return;

    if (!current || current.quantity === 0) {
      setCartMap(prev => ({ ...prev, [food.food_id]: { quantity: 0, cartId: 0, loading: true } }));
      try {
        const res = await authenticatedFetch(CART_ADD_URL, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ food_id: food.food_id, quantity: 1, replace_cart: false }),
        });
        const json = await res.json();
        if (res.ok && json.success) {
          trackFood('add_to_cart', food.food_name);
          await syncCart();
          onCartChange?.();
        } else if (res.status === 409 || (json.message && json.message.toLowerCase().includes('different kitchen'))) {
          setCartMap(prev => { const n = { ...prev }; delete n[food.food_id]; return n; });
          showNotification?.('warning', 'Different Kitchen', 'Clear your current cart to order from this kitchen.');
        } else {
          setCartMap(prev => { const n = { ...prev }; delete n[food.food_id]; return n; });
          showNotification?.('error', 'Failed', json.message || 'Could not add to cart.');
        }
      } catch {
        setCartMap(prev => { const n = { ...prev }; delete n[food.food_id]; return n; });
        showNotification?.('error', 'Network Error', 'Please check your connection.');
      }
    } else {
      const newQty = current.quantity + 1;
      setCartMap(prev => ({ ...prev, [food.food_id]: { ...current, loading: true } }));
      try {
        const res = await authenticatedFetch(CART_UPDATE_URL(current.cartId), {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ quantity: newQty }),
        });
        const json = await res.json();
        if (res.ok && json.success) {
          setCartMap(prev => ({ ...prev, [food.food_id]: { ...current, quantity: newQty, loading: false } }));
          onCartChange?.();
        } else {
          setItemLoading(food.food_id, false);
        }
      } catch {
        setItemLoading(food.food_id, false);
      }
    }
  };

  const handleDecrement = async (food: DiscoveryFood) => {
    const current = cartMap[food.food_id];
    if (!current || current.quantity === 0 || current.loading) return;

    setCartMap(prev => ({ ...prev, [food.food_id]: { ...current, loading: true } }));

    if (current.quantity === 1) {
      try {
        const res = await authenticatedFetch(CART_REMOVE_ITEM_URL(current.cartId), { method: 'DELETE' });
        const json = await res.json();
        if (res.ok && json.success) {
          setCartMap(prev => { const n = { ...prev }; delete n[food.food_id]; return n; });
          onCartChange?.();
        } else {
          setItemLoading(food.food_id, false);
        }
      } catch {
        setItemLoading(food.food_id, false);
      }
    } else {
      const newQty = current.quantity - 1;
      try {
        const res = await authenticatedFetch(CART_UPDATE_URL(current.cartId), {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ quantity: newQty }),
        });
        const json = await res.json();
        if (res.ok && json.success) {
          setCartMap(prev => ({ ...prev, [food.food_id]: { ...current, quantity: newQty, loading: false } }));
          onCartChange?.();
        } else {
          setItemLoading(food.food_id, false);
        }
      } catch {
        setItemLoading(food.food_id, false);
      }
    }
  };

  const fetchFoods = useCallback(async (tab: Tab, r: number) => {
    setIsLoading(true);
    setError(null);
    setFoods([]);
    setVisibleCount(8);
    try {
      const coords = getStoredCoords();
      const lat = coords?.lat;
      const lon = coords?.lon;

      let url = '';
      if (tab === 'recommended') url = RECOMMENDED_FOODS_URL(lat, lon, r, 50);
      else if (tab === 'top_rated') url = TOP_RATED_FOODS_URL(lat, lon, r);
      else url = TRENDING_FOODS_URL(lat, lon, r, 50);

      const fetcher = tab === 'recommended' ? authenticatedFetch : fetch;
      const res = await fetcher(url, { headers: { accept: 'application/json' } });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      const list: DiscoveryFood[] = data.recommendations ?? data.foods ?? [];
      const safeList = list.filter((f) => {
        const k = f.kitchen as any;
        if (!k) return true;
        if (k.status === false) return false;
        if (k.is_approved === false) return false;
        return true;
      });
      setFoods(safeList);
    } catch {
      setError('Could not load foods. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchFoods(activeTab, radius);
  }, [activeTab, fetchFoods, locationVersion, radius]);

  useEffect(() => {
    const handler = () => fetchFoods(activeTab, radius);
    window.addEventListener('foodAvailabilityChanged', handler);
    window.addEventListener('kitchenApprovalChanged', handler);
    return () => {
      window.removeEventListener('foodAvailabilityChanged', handler);
      window.removeEventListener('kitchenApprovalChanged', handler);
    };
  }, [activeTab, fetchFoods, radius]);

  const getImageUrl = (food: DiscoveryFood): string => {
    if (food.food_image) return getFirstImageUrl(food.food_image);
    return getFirstImageUrl(food.image_urls);
  };

  const handleRadiusChange = (r: number) => {
    setRadius(r);
    localStorage.setItem('foodDiscoveryRadius', String(r));
  };

  const visibleFoods = foods.slice(0, visibleCount);
  const hasMore = foods.length > visibleCount;

  const tabs: { key: Tab; label: string; Icon: React.ElementType }[] = [
    { key: 'recommended', label: 'For You', Icon: Award },
    { key: 'top_rated', label: 'Top Rated', Icon: Star },
    { key: 'trending', label: 'Trending', Icon: TrendingUp },
  ];

  return (
    <section className="fd-section">
      <div className="fd-header">
        <div className="fd-title-group">
          <h2 className="fd-title">Discover Food</h2>
          <p className="fd-subtitle">Personalized picks, fan favorites, and what's hot right now</p>
        </div>
        <div className="fd-tabs">
          {tabs.map(({ key, label, Icon }) => (
            <button
              key={key}
              className={`fd-tab${activeTab === key ? ' fd-tab--active' : ''}`}
              onClick={() => setActiveTab(key)}
            >
              <Icon size={15} />
              {label}
            </button>
          ))}
        </div>
      </div>
      <div className="fd-controls">
        <label className="fd-radius-label">Radius:</label>
        <select
          className="fd-radius-select"
          value={radius}
          onChange={e => handleRadiusChange(Number(e.target.value))}
        >
          {[5, 10, 15, 20, 25, 30].map(r => (
            <option key={r} value={r}>{r} km</option>
          ))}
        </select>
      </div>

      {isLoading && (
        <div className="fd-loading">
          <div className="fd-spinner" />
          <span>Loading...</span>
        </div>
      )}

      {error && !isLoading && (
        <div className="fd-error">{error}</div>
      )}

      {!isLoading && !error && foods.length === 0 && (
        <div className="fd-empty">No foods available right now. Check back soon!</div>
      )}

      {!isLoading && !error && foods.length > 0 && (
        <>
        <div className="fd-grid">
          {visibleFoods.map(food => {
            const imgUrl = getImageUrl(food);
            const cartItem = cartMap[food.food_id];
            const qty = cartItem?.quantity ?? 0;
            const cartLoading = cartItem?.loading ?? false;
            const soldOut = food.available_quantity === 0 || food.stock_status === 'Sold Out';

            return (
              <div key={food.food_id} className="fd-card">
                <div className="fd-card-img-wrap">
                  <img
                    src={imgUrl}
                    alt={food.food_name}
                    className="fd-card-img"
                    onError={e => { (e.target as HTMLImageElement).src = FALLBACK; }}
                  />
                  <div className="fd-card-overlay" />
                  {food.category && (
                    <span className="fd-category-badge">{food.category}</span>
                  )}
                  {food.average_rating !== undefined && food.average_rating > 0 && (
                    <span className="fd-rating-badge">
                      <Star size={11} fill="currentColor" />
                      {food.average_rating.toFixed(1)}
                      {food.total_reviews !== undefined && food.total_reviews > 0 && (
                        <span className="fd-reviews"> ({food.total_reviews})</span>
                      )}
                    </span>
                  )}
                  {activeTab === 'trending' && food.total_orders !== undefined && food.total_orders > 0 && (
                    <span className="fd-trending-badge">
                      <TrendingUp size={11} />
                      {food.total_orders} orders
                    </span>
                  )}
                  {food.price !== undefined && (
                    <span className="fd-price-badge">£{food.price}</span>
                  )}
                </div>

                <div className="fd-card-body">
                  <h4 className="fd-card-name">{food.food_name}</h4>
                  {food.kitchen && (
                    <p className="fd-card-kitchen">by {food.kitchen.kitchen_name}</p>
                  )}
                  {food.description && (
                    <p className="fd-card-desc">{food.description.length > 70 ? `${food.description.substring(0, 70)}...` : food.description}</p>
                  )}
                  <div className="fd-card-badges">
                    {food.badges?.map(b => (
                      <span key={b} className={`fd-badge fd-badge--${b.toLowerCase().replace(/\s+/g, '-')}`}>{b}</span>
                    ))}
                    {food.stock_status && food.stock_status !== 'In Stock' && (
                      <span className="fd-badge fd-badge--stock">{food.stock_status}</span>
                    )}
                    {food.delivery_status && (
                      <span className="fd-badge fd-badge--delivery"><Zap size={10} /> {food.delivery_status}</span>
                    )}
                  </div>
                  <div className="fd-card-meta">
                    {food.distance_km !== undefined && food.distance_km !== null && (
                      <span className="fd-meta-item"><Navigation size={12} /> {food.distance_km.toFixed(1)} km</span>
                    )}
                    {food.eta && (
                      <span className="fd-meta-item"><Clock size={12} /> {food.eta}</span>
                    )}
                    {food.available_quantity !== undefined && food.available_quantity > 0 && (
                      <span className="fd-meta-item"><Package size={12} /> {food.available_quantity} left</span>
                    )}
                  </div>
                  <div className="fd-card-footer">
                    <div className="fd-card-price-row">
                      {food.price !== undefined && (
                        <span className="fd-card-price">£{food.price}</span>
                      )}
                      {activeTab === 'recommended' && food.recommendation_score !== undefined && food.recommendation_score > 0 && (
                        <span className="fd-score fd-score--rec"><Flame size={11} /> {Math.round(food.recommendation_score)}% match</span>
                      )}
                      {activeTab === 'trending' && food.trending_score !== undefined && food.trending_score > 0 && (
                        <span className="fd-score fd-score--trend"><TrendingUp size={11} /> {Math.round(food.trending_score)} trending</span>
                      )}
                    </div>
                    <div className="fd-card-actions">
                      <button
                        className="fd-info-btn"
                        onClick={() => setDetailFood(food)}
                        title="View details & reviews"
                      >
                        <Info size={14} />
                      </button>
                      {isLoggedIn && food.price !== undefined && !soldOut && (
                        qty > 0 ? (
                          <div className="fd-qty-controls">
                            <button
                              className="fd-qty-btn"
                              onClick={() => handleDecrement(food)}
                              disabled={cartLoading}
                            >
                              {cartLoading ? <Loader size={12} className="fd-spin" /> : <Minus size={14} />}
                            </button>
                            <span className="fd-qty-val">{qty}</span>
                            <button
                              className="fd-qty-btn"
                              onClick={() => handleIncrement(food)}
                              disabled={cartLoading}
                            >
                              {cartLoading ? <Loader size={12} className="fd-spin" /> : <Plus size={14} />}
                            </button>
                          </div>
                        ) : (
                          <button
                            className="fd-add-btn"
                            onClick={() => handleIncrement(food)}
                            disabled={cartLoading}
                          >
                            {cartLoading
                              ? <Loader size={14} className="fd-spin" />
                              : <ShoppingCart size={14} />}
                            {cartLoading ? 'Adding...' : 'Add to Cart'}
                          </button>
                        )
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
        {hasMore && (
          <div className="fd-load-more-wrap">
            <button className="fd-load-more" onClick={() => setVisibleCount(c => c + 8)}>
              Load More ({foods.length - visibleCount} remaining)
            </button>
          </div>
        )}
        </>
      )}
      {detailFood && (
        <FoodDetailModal
          foodId={detailFood.food_id}
          onClose={() => setDetailFood(null)}
          showNotification={showNotification}
          onCartChange={onCartChange}
        />
      )}
    </section>
  );
};

export default FoodDiscovery;
